<script lang="ts" setup>
import type { PagesDocumentsChildrenEmitsValue } from '~/components/pages/documents/PagesDocumentsChildren.vue'
import { ApiDocuments } from '~~/server/api/documents'

const route = useRoute()
const router = useRouter()
const { $t } = useNuxtApp()

// Accessibility: Screen reader announcement for content changes (WCAG 4.1.2)
const contentAnnouncement = ref<string>('')

const {
    data: page,
    pending,
    redirect
} = await useFetchWithRedirect<ApiDocuments>(
    () =>
        `/api/documents?category_level_0=${
            route.query.td || ''
        }&category_level_1=${route.query.k || ''}&category_level_2=${
            route.query.p || ''
        }&date_from=${route.query.do || ''}`,
    {
        route,
    }
)

if(redirect && !redirect?.error) {
    navigateTo(redirect.data.url, redirect.data.options)
}

const handleChange = ({
    value,
    key,
}: PagesDocumentsChildrenEmitsValue): void => {
    const query: Record<string, any> = {
        ...route.query,
        td: route.query.td || page.value?.data.category_level_0.slug,
    }
    if (!value) {
        delete query[key]
    } else {
        query[key] = value
    }
    router.push({
        query: { ...query },
        hash: route.hash,
    })
}
const typeDocumentsTitle = $t('Typ dokumentov:')

// Watch for content changes and announce to screen readers (WCAG 4.1.2)
watch([() => page.value?.data.category_level_0?.title, () => page.value?.data.category_level_1?.title, pending], ([category, subcategory, isPending]) => {
    if (!isPending && category) {
        contentAnnouncement.value = ''
        nextTick(() => {
            const categoryText = category || ''
            const subcategoryText = subcategory ? `, ${subcategory}` : ''
            contentAnnouncement.value = `${$t('Zobrazené dokumenty:')} ${categoryText}${subcategoryText}`
        })
    }
})

// Schema.org
useSchemaOrg([
    defineWebPage({
        name: 'Dokumenty',
    }),
])

if (Object.keys(route.query).length > 0) {
    useCanonicalHead({})
}

// Page meta
usePageMeta({
    meta: page.value?.data.meta,
    title: page.value?.data.title,
})
</script>
<template>
  <PagesDocumentsPageHeader
    v-if="page"
    :title="page.data.title"
  />
  <ContainerFluid
    v-if="page"
    class="my-12 my-md-20"
  >
    <article>
      <div class="row">
        <div class="col-12 col-lg-3">
          <Tracker :data="{ position: typeDocumentsTitle }">
            <WidgetMenu
              class="mb-2 mb-md-0 mt-md-8"
              :data="page.data.top_level_categories"
              :active-slug="page.data.category_level_0.slug"
              active-slug-key="td"
              :external-link="false"
              :headline="typeDocumentsTitle"
            />
          </Tracker>
        </div>
        <div
          id="documents-content"
          :key="page.data.category_level_0.slug"
          class="col-12 col-lg-9 position-relative"
          tabindex="-1"
          style="outline: none;"
        >
          <Card
            classes="rounded-4 p-4 p-md-8"
            background-color="white"
            :aria-label="`${$t('Dokumenty typu:')} ${page.data.category_level_0.title}`"
          >
            <PagesDocumentsCategories
              v-if="page.data.categories"
              class="mb-4 mb-md-6"
              :data="page.data"
            />
            <PagesDocumentsChildren
              :data="page.data"
              @click="(value: PagesDocumentsChildrenEmitsValue) => handleChange(value)"
            />
            <PagesDocumentsTabs
              :data="page.data"
              @date-update="(value: PagesDocumentsChildrenEmitsValue) => handleChange(value)"
            />
          </Card>
          <LoadingBar
            v-if="pending"
            color="white"
          />
        </div>
      </div>
    </article>
    <IconsWrapper>
      <IconsSymbolSort />
    </IconsWrapper>
    <!-- Screen reader announcement for content changes (WCAG 4.1.2) -->
    <div
      aria-live="polite"
      aria-atomic="true"
      class="visually-hidden"
    >
      {{ contentAnnouncement }}
    </div>
  </ContainerFluid>
</template>
